/**
 * Typed HTTP client for the BEAM backend API.
 *
 * - Attaches Bearer token from the session store (read via a lazy accessor so
 *   this module does not create a circular dependency with src/auth/session.ts).
 * - All request/response shapes come from @beam/api-types — never duplicated here.
 * - Parses error envelopes ({error:{code,message}}) via the ApiError zod schema and
 *   throws a plain Error whose .message is the backend's error.message and which
 *   carries the error code on .cause for callers that need it.
 * - Response bodies are validated with zod schemas from @beam/api-types so the app
 *   fails loudly on contract drift rather than silently mis-typing data.
 */

import {
  ApiError,
  ChangePasswordRequest,
  DeleteAccountRequest,
  DeleteAccountResponse,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  MeResponse,
  ResetPasswordRequest,
  SignupRequest,
  SignupResponse,
  SseTicketResponse,
} from '@beam/api-types';
import type { LoginRequest as LoginRequestType, SignupRequest as SignupRequestType } from '@beam/api-types';
import { Platform } from 'react-native';
import { API_BASE_URL } from '../config';

const IS_WEB = Platform.OS === 'web';

// ---------------------------------------------------------------------------
// Token accessor — injected by src/auth/session.ts at startup to avoid a
// circular import (client ← session ← client).
// ---------------------------------------------------------------------------
let _getToken: (() => string | null) | null = null;

export function setTokenAccessor(fn: () => string | null): void {
  _getToken = fn;
}

/**
 * Current access token (or null). Exposed for the few flows that must build their OWN request
 * rather than go through `request()` — e.g. downloading a binary PDF (GET /api/invoices/:id/pdf),
 * where the JSON-parsing wrapper doesn't apply. Authenticated like every other call (Bearer).
 */
export function getAccessToken(): string | null {
  return _getToken?.() ?? null;
}

// Invoked when an AUTHENTICATED request is rejected with 401 (token expired/invalid). Registered
// by src/auth/session.ts to clear the session so the AuthGate redirects to /login — rather than
// leaving the user stranded on a "Token expired" screen. NOT fired for the login call itself
// (which sends no token), so a bad-credentials 401 still surfaces "Invalid email or password".
let _onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: () => void): void {
  _onUnauthorized = fn;
}

// W0 — refresh-token handler. Registered by src/auth/session.ts. On a 401 with a token, the client
// asks this to mint a fresh access token (rotating the refresh token) and retries the request once
// before giving up. Returns the new access token, or null if refresh failed (→ sign out).
let _refresh: (() => Promise<string | null>) | null = null;

export function setRefreshHandler(fn: () => Promise<string | null>): void {
  _refresh = fn;
}

// W1 (web) — the CSRF token (returned by login/refresh) the browser client echoes in X-CSRF-Token on
// the state-changing auth routes (/refresh, /logout). Registered by src/auth/session.ts. Native: unused.
let _getCsrf: (() => string | null) | null = null;

export function setCsrfAccessor(fn: () => string | null): void {
  _getCsrf = fn;
}

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

export class ApiRequestError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  retried = false,
): Promise<T> {
  const token = _getToken?.() ?? null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Web — echo the CSRF token on the state-changing auth routes (the refresh token lives in an
  // httpOnly cookie there, not in JS). Native clients send the refresh token in the body instead.
  if (IS_WEB && (path === '/api/auth/refresh' || path === '/api/auth/logout')) {
    headers['X-CSRF-Token'] = _getCsrf?.() ?? '';
  }

  const url = `${API_BASE_URL}${path}`;
  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    // Web — send the httpOnly refresh cookie to /api/auth/*. Native: no cookies.
    credentials: IS_WEB ? 'include' : 'same-origin',
  });

  const text = await res.text();

  // Parse JSON — all BEAM endpoints return JSON (including errors).
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    throw new ApiRequestError(
      'parse_error',
      `Non-JSON response from ${method} ${path}: ${text.slice(0, 200)}`,
      res.status,
    );
  }

  // Check for the standard error envelope first.
  if (!res.ok) {
    // Session expiry: a 401 on a request that carried a token means the token lapsed/was revoked.
    // Trigger re-auth (clears the session → AuthGate routes to /login). Still throw so the caller
    // can react. Login (no token) is excluded, so its 401 stays a normal credentials error.
    if (res.status === 401 && token) {
      // Try a one-time refresh-and-retry before giving up (W0). The refresh call itself is excluded
      // to avoid recursion. On refresh success the retried request picks up the new token.
      if (!retried && _refresh && path !== '/api/auth/refresh') {
        let newToken: string | null = null;
        try {
          newToken = await _refresh();
        } catch {
          newToken = null;
        }
        if (newToken) {
          return request<T>(method, path, body, true);
        }
      }
      _onUnauthorized?.();
    }
    const parsed = ApiError.safeParse(json);
    if (parsed.success) {
      throw new ApiRequestError(
        parsed.data.error.code,
        parsed.data.error.message,
        res.status,
      );
    }
    // Unexpected error shape — surface status + raw text.
    throw new ApiRequestError(
      'http_error',
      `HTTP ${res.status} from ${method} ${path}`,
      res.status,
    );
  }

  return json as T;
}

// ---------------------------------------------------------------------------
// Typed helpers
// ---------------------------------------------------------------------------

export async function get<T>(path: string): Promise<T> {
  return request<T>('GET', path);
}

export async function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, body);
}

export async function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PUT', path, body);
}

export async function patch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PATCH', path, body);
}

export async function del<T>(path: string): Promise<T> {
  return request<T>('DELETE', path);
}

// ---------------------------------------------------------------------------
// Auth endpoints (confirmed routes from beam-backend/src/api/authRouter.ts)
//   POST /api/auth/login  → LoginResponse
//   GET  /api/auth/me     → MeResponse  (requires Bearer token)
// ---------------------------------------------------------------------------

export async function login(
  email: LoginRequestType['email'],
  password: LoginRequestType['password'],
  client?: 'web' | 'ios' | 'android',
): Promise<LoginResponse> {
  // Validate the outbound shape with the zod schema (catches typos early).
  const body = LoginRequest.parse({ email, password, ...(client ? { client } : {}) });
  const raw = await post<unknown>('/api/auth/login', body);
  return LoginResponse.parse(raw);
}

export async function me(): Promise<MeResponse> {
  const raw = await get<unknown>('/api/auth/me');
  return MeResponse.parse(raw);
}

/**
 * In-app subscriptions (2026-06-19) — POST /api/public/signup (public, no auth).
 * Creates an org + owner in a LOCKED (incomplete) state and returns the same auth payload as
 * login (access + refresh token), so the app is signed in immediately and can route to the paywall.
 */
export async function signup(input: SignupRequestType): Promise<SignupResponse> {
  const body = SignupRequest.parse(input);
  const raw = await post<unknown>('/api/public/signup', body);
  return SignupResponse.parse(raw);
}

/** W0/W1 — exchange a refresh token for a fresh access token (rotates it). Native passes the token;
 *  web passes nothing (the refresh token rides the httpOnly cookie + X-CSRF-Token header). */
export async function refresh(refreshToken?: string): Promise<LoginResponse> {
  const raw = await post<unknown>('/api/auth/refresh', refreshToken ? { refreshToken } : {});
  return LoginResponse.parse(raw);
}

/** W0 — revoke the current session server-side (best-effort; the caller clears local state). */
export async function logout(refreshToken?: string): Promise<void> {
  try {
    await post<unknown>('/api/auth/logout', refreshToken ? { refreshToken } : {});
  } catch {
    // best-effort — local sign-out proceeds regardless
  }
}

/**
 * Account deletion (App Store / Google Play requirement). POST /api/account/delete is
 * authenticated + owner-only and requires the caller to re-enter their password. On success the
 * org and all its data are removed server-side; the caller (session.deleteAccount) then wipes the
 * local session. The outbound body is validated by DeleteAccountRequest and the result by
 * DeleteAccountResponse so a contract change fails loudly.
 */
export async function deleteAccount(password: string): Promise<DeleteAccountResponse> {
  const body = DeleteAccountRequest.parse({ password });
  const raw = await post<unknown>('/api/account/delete', body);
  return DeleteAccountResponse.parse(raw);
}

/**
 * Public — request a password-reset email. The backend ALWAYS answers { ok: true } (no user
 * enumeration), so there is nothing meaningful to return; resolves once the request is accepted.
 */
export async function forgotPassword(email: string, client?: 'web' | 'ios' | 'android'): Promise<void> {
  const body = ForgotPasswordRequest.parse({ email, ...(client ? { client } : {}) });
  await post<unknown>('/api/auth/forgot-password', body);
}

/** Public — complete a reset with the emailed token → signs the user in (returns a LoginResponse). */
export async function resetPassword(
  token: string,
  password: string,
  client?: 'web' | 'ios' | 'android',
): Promise<LoginResponse> {
  const body = ResetPasswordRequest.parse({ token, password, ...(client ? { client } : {}) });
  return LoginResponse.parse(await post<unknown>('/api/auth/reset-password', body));
}

/** Authed — change the current password (verify current). Returns a fresh session (LoginResponse). */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  client?: 'web' | 'ios' | 'android',
): Promise<LoginResponse> {
  const body = ChangePasswordRequest.parse({ currentPassword, newPassword, ...(client ? { client } : {}) });
  return LoginResponse.parse(await post<unknown>('/api/auth/change-password', body));
}

/** W3 — mint a short-lived single-purpose SSE ticket (Bearer-authed) for the EventSource URL,
 * so the full access token never goes in the query string. Returns null on failure. */
export async function sseTicket(): Promise<string | null> {
  try {
    const parsed = SseTicketResponse.safeParse(await post<unknown>('/api/auth/sse-ticket', {}));
    return parsed.success ? parsed.data.ticket : null;
  } catch {
    return null;
  }
}
